import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Button, type ButtonSize, type ButtonVariant } from "./Button";

// Simple click-triggered dropdown. Not a full popover library — just
// enough to host the "Maintain" menu on the case header and any filter
// picker that doesn't fit in a native <select>. Outside-click and
// Escape close the menu; Tab out of the last item closes too.

export interface DropdownItem {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  // Optional leading icon or emoji; rendered inline before the label.
  leading?: ReactNode;
  // Optional tone: "danger" colours the item red; used for destructive
  // items inside a maintain menu.
  tone?: "default" | "danger";
}

interface Props {
  trigger: ReactNode;
  items: DropdownItem[];
  variant?: ButtonVariant;
  size?: ButtonSize;
  align?: "start" | "end";
  disabled?: boolean;
  hideCaret?: boolean;
  ariaLabel?: string;
}

export const Dropdown = forwardRef<HTMLDivElement, Props>(function Dropdown(
  {
    trigger,
    items,
    variant = "secondary",
    size = "md",
    align = "end",
    disabled,
    hideCaret = false,
    ariaLabel,
  },
  forwardedRef,
) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  useImperativeHandle(forwardedRef, () => rootRef.current as HTMLDivElement);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={`ui-dropdown ui-dropdown-align-${align}`}
    >
      <Button
        variant={variant}
        size={size}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        trailing={
          hideCaret ? undefined : (
            <span className="ui-dropdown-caret" aria-hidden>▾</span>
          )
        }
      >
        {trigger}
      </Button>
      {open && (
        <div className="ui-dropdown-menu" role="menu">
          {items.map((item, i) => (
            <button
              key={`${i}-${item.label}`}
              type="button"
              role="menuitem"
              className={`ui-dropdown-item${
                item.tone === "danger" ? " ui-dropdown-item-danger" : ""
              }`}
              onClick={() => {
                if (item.disabled) return;
                setOpen(false);
                item.onSelect();
              }}
              disabled={item.disabled}
            >
              {item.leading !== undefined && (
                <span className="ui-dropdown-item-leading" aria-hidden>
                  {item.leading}
                </span>
              )}
              <span className="ui-dropdown-item-label">{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
